/**
 * Pure rules about a chat entry — who sent it, how it's labelled and timed,
 * whether it's still optimistic, and what this viewer may do to it — shared
 * by the bubble layout (ChatThread) and the note-card layout (NoteCards).
 */
import type { EntryAttachment } from '@discovr/types';

import { yearOptionFor } from '../../../lib/datetime';
import type { ReviewThreadEntry } from '../../../lib/models';

/** `local_frame_url` is a client-only snapshot of the staged frame, taken when the optimistic bubble is created; never sent. */
export type ChatThreadEntry = ReviewThreadEntry & { local_frame_url?: string };

/** A message longer than this clamps to four lines behind Read more. */
const BODY_CLAMP_CHARS = 260;

/** Per-message actions, named the same in the bubble and the card layout. */
export const EDIT_MESSAGE_LABEL = 'Edit message';
export const EDIT_TITLE = 'Edit';
export const DELETE_MESSAGE_LABEL = 'Delete message';
export const DELETE_TITLE = 'Delete';

/** The viewer's own messages are labelled this instead of their name. */
export const OWN_SENDER_LABEL = 'You';

/** Kind of the system entry a rejection writes into the thread. */
const REJECTION_KIND = 'rejection';

const MAX_INITIALS = 2;
const UNKNOWN_INITIAL = '?';

export function isRejectionEntry(entry: Pick<ChatThreadEntry, 'kind'>): boolean {
  return entry.kind === REJECTION_KIND;
}

export function isLongBody(body: string): boolean {
  return body.length > BODY_CLAMP_CHARS;
}

/** Sub-roles collapse to one side, so a teammate's message never renders on the other side. */
function normalizeSide(actor: string): string {
  if (!actor) return 'system';
  if (actor === 'brand' || actor.startsWith('brand_')) return 'brand';
  if (actor === 'creator' || actor === 'talent_manager') return 'creator';
  if (actor === 'admin' || actor === 'admin_member' || actor === 'super_admin') return 'admin';
  return actor;
}

export function isMine(actor: string, mine: string): boolean {
  return normalizeSide(actor) === normalizeSide(mine);
}

export function actorLabel(actor: string): string {
  switch (actor) {
    case 'brand':
    case 'brand_owner':
      return 'Owner';
    case 'brand_admin':
      return 'Admin';
    case 'brand_member':
      return 'Brand Member';
    case 'admin':
    case 'admin_member':
    case 'super_admin':
      return 'Discovr Team';
    case 'creator':
      return 'Creator';
    case 'talent_manager':
      return 'Talent Manager';
    case 'ai':
      return 'AI';
    case 'legacy':
      return 'Note';
    default:
      return actor || 'System';
  }
}

/** "Name · Org" (the server stamps the org into sender_title); the org alone, then a role label, when parts are blank. */
export function senderDisplayName(entry: {
  actor: string;
  sender_name?: string;
  sender_title?: string;
}): string {
  const person = entry.sender_name?.trim();
  const org = entry.sender_title?.trim();
  if (person && org) return `${person} · ${org}`;
  return person || org || actorLabel(entry.actor);
}

/** The sender label a message shows this viewer: "You" for their own side. */
export function senderLabelFor(entry: ChatThreadEntry, outgoingActor: string): string {
  if (isMine(entry.actor, outgoingActor)) return OWN_SENDER_LABEL;
  return senderDisplayName(entry);
}

/** Up to two initials for an avatar without a photo: "Tanya Malhotra" → "TM". The person's own name wins over the label shown. */
export function senderInitials(entry: Pick<ChatThreadEntry, 'sender_name'>, label: string): string {
  const source = entry.sender_name?.trim() || label;
  const initials = source
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, MAX_INITIALS)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
  return initials || UNKNOWN_INITIAL;
}

export function formatTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function formatDayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(d, now)) return 'Today';
  if (isSameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: yearOptionFor(d, now),
  });
}

export function dayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** A files-only message has no empty text line. */
export function showsBodyText(body: string, attachments: EntryAttachment[]): boolean {
  return !!body.trim() || attachments.length === 0;
}


const OPTIMISTIC_ID_PREFIX = 'optimistic-';

/** A fresh id for an optimistic (not-yet-persisted) bubble. */
export function newOptimisticEntryId(): string {
  return `${OPTIMISTIC_ID_PREFIX}${Date.now()}`;
}

export function isOptimisticEntry(id: string): boolean {
  return id.startsWith(OPTIMISTIC_ID_PREFIX);
}

/** What the thread allows overall; each message's own server grants are on the entry. */
export interface MessageActionGates {
  enableReplies: boolean;
  canAddComment: boolean;
  canPost: boolean;
  canEditAny: boolean;
  canDeleteAny: boolean;
  threadLocked: boolean;
  readOnly: boolean;
  viewerIsSuperAdmin: boolean;
}

export interface MessagePermissions {
  canReply: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

/**
 * The server grants Edit/Delete (viewer_can_*); the client adds only what it
 * can't see: lock, read-only and optimistic state. Super Admin may delete
 * through a lock, as the server allows.
 */
export function messagePermissions(entry: ChatThreadEntry, gates: MessageActionGates): MessagePermissions {
  const open = !isOptimisticEntry(String(entry.id)) && !gates.readOnly;
  return {
    canReply: open && gates.enableReplies && gates.canAddComment && gates.canPost,
    canEdit: open && !!entry.viewer_can_edit && gates.canEditAny && !gates.threadLocked,
    canDelete: open && !!entry.viewer_can_delete && gates.canDeleteAny && (gates.viewerIsSuperAdmin || !gates.threadLocked),
  };
}

export function hasAnyAction({ canReply, canEdit, canDelete }: MessagePermissions): boolean {
  return canReply || canEdit || canDelete;
}
